import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { AuthSession } from "../../auth/keycloak.js";
import { usePmNav } from "./pm-nav.js";
import { PmLanding } from "./PmLanding.js";
import { PmMsView } from "./PmMsView.js";
import { usePmOverview } from "./usePmBoard.js";
import { PmNoticesProvider } from "./PmNotices.js";
import { PmInstitutesProvider } from "./pm-institutes.js";

/**
 * The board at `/pm` (ADR-055 D1) — mounted by `App` instead of the facility navigator when the path is `/pm` and `/me`
 * says the caller has access (the API decides the same with a 404, so the UI never offers what it cannot load).
 * `arc_pm_ms` picks the micro-scenario; without it, the landing page.
 */
export function PmBoard({ session, meId }: { session: AuthSession; meId: number }) {
  return (
    <PmInstitutesProvider realm={session.realm}>
      <PmNoticesProvider>
        <PmBoardContent session={session} meId={meId} />
      </PmNoticesProvider>
    </PmInstitutesProvider>
  );
}

function PmBoardContent({ session, meId }: { session: AuthSession; meId: number }) {
  const { t } = useTranslation();
  const nav = usePmNav();
  const overview = usePmOverview(session);

  if (nav.msId !== null) {
    return (
      <PmMsView
        session={session}
        meId={meId}
        msId={nav.msId}
        view={nav.view}
        taskId={nav.taskId}
        onView={(v) => nav.openView(nav.msId!, v)}
        onOpenTask={(id) => nav.openTask(nav.msId!, nav.view, id)}
        onCloseTask={() => nav.closeTask(nav.msId!, nav.view)}
        onBack={nav.backToLanding}
      />
    );
  }
  if (overview.isPending) {
    return (
      <p role="status" className="text-muted-foreground p-6 text-sm">
        {t("pmBoard.loading")}
      </p>
    );
  }
  if (overview.isError) {
    return (
      <div className="flex flex-col items-start gap-3 p-6">
        <h1 className="sr-only">{t("pmBoard.title")}</h1>
        <p role="alert" className="text-destructive text-sm">
          {t("pmBoard.loadFailed")}
        </p>
        <Button variant="outline" size="sm" onPress={() => void overview.refetch()} isDisabled={overview.isFetching}>
          {t("pmBoard.tryAgain")}
        </Button>
      </div>
    );
  }
  return <PmLanding overview={overview.data} onOpen={nav.openMs} />;
}
